import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CRMSharingRulesService } from "@/services/crm-sharing-rules";
import { DRQConditionUI, convertToDRQFilterInput } from "@/types/crm/drq-builder";
import { AnyDRQQuery, DRQBuilder, serializeDRQQuery, validateDRQQuery } from "@/utils/drq";
import { AlertCircle, CheckCircle, Play, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface DRQPreviewProps {
    conditions: DRQConditionUI[];
    referenceTypeId?: number;
    queryType: "user" | "object";
    title: string;
    /** Pre-built DRQ query (e.g. from the hook, already including filter logic). When provided, takes
     *  priority over the DRQ generated from `conditions` so that OR / custom logic is preserved. */
    drqQuery?: AnyDRQQuery | {};
}

export function DRQPreview({ conditions, referenceTypeId, queryType, title, drqQuery: drqQueryProp }: DRQPreviewProps) {
    const [isValidating, setIsValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<{
        valid: boolean;
        count?: number;
        message?: string;
        error?: string;
    } | null>(null);

    // Use the pre-built DRQ when available (preserves filter logic), otherwise build from conditions.
    const generateDRQ = () => {
        if (drqQueryProp && Object.keys(drqQueryProp).length > 0) {
            return drqQueryProp as AnyDRQQuery;
        }
        if (conditions.length === 0) {
            return null;
        }
        const filterInputs = conditions.map(convertToDRQFilterInput);
        return DRQBuilder.buildFromFilterInputs(filterInputs);
    };

    const drqQuery = generateDRQ();
    const jsonString = serializeDRQQuery(drqQuery || {});
    const isValid = validateDRQQuery(drqQuery || {});

    const handleValidate = async () => {
        if (!drqQuery || !referenceTypeId) {
            toast.error("Impossible to validate: missing data");
            return;
        }

        setIsValidating(true);
        setValidationResult(null);

        try {
            const result = await CRMSharingRulesService.validateDRQQuery(drqQuery, referenceTypeId, queryType);
            setValidationResult(result);

            if (result.valid) {
                toast.success("DRQ query is valid");
            } else {
                toast.error(`Validation error: ${result.error}`);
            }
        } catch (error) {
            toast.error("Error during validation");
            setValidationResult({
                valid: false,
                error: "Connection error"
            });
        } finally {
            setIsValidating(false);
        }
    };

    return (
        <div className="mt-4 flex flex-col gap-4 rounded-md border bg-white p-4">
            <div className="flex items-center gap-2">
                <div className="text-sm font-medium">{title}</div>
                <Badge variant={isValid ? "default" : "destructive"}>{isValid ? "Syntax valid" : "Syntax invalid"}</Badge>
            </div>
            <div className="space-y-4">
                {/* Validation côté serveur */}
                {validationResult && (
                    <Alert variant={validationResult.valid ? "default" : "destructive"}>
                        {validationResult.valid ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        <AlertTitle>{validationResult.valid ? `The query is valid` : validationResult.error || "Validation error"}</AlertTitle>
                        <AlertDescription>{validationResult.count !== undefined ? `Number of results: ${validationResult.count}` : ""}</AlertDescription>
                    </Alert>
                )}

                {/* Bouton de validation */}
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {conditions.length} condition{conditions.length > 1 ? "s" : ""}
                    </div>
                    <Button onClick={handleValidate} disabled={!isValid || !referenceTypeId || isValidating} size="sm">
                        <Play className="mr-2 h-4 w-4" />
                        {isValidating ? "Validation..." : "Test the query"}
                    </Button>
                </div>

                {/* Prévisualisation JSON */}
                <div className="space-y-2">
                    <div className="text-sm font-medium">JSON preview:</div>
                    <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">{jsonString || "{}"}</pre>
                </div>

                {/* Avertissements */}
                {conditions.length === 0 && (
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>No condition defined. The query will be empty.</AlertDescription>
                    </Alert>
                )}

                {!isValid && conditions.length > 0 && (
                    <Alert variant="destructive" className="flex items-center">
                        <AlertCircle className="flex h-4 w-4 items-center" />
                        <AlertDescription className="flex items-center">The query syntax is invalid. Please check the conditions.</AlertDescription>
                    </Alert>
                )}
            </div>
        </div>
    );
}
